import dayjs from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'

import type { createEventParams } from 'supabase/events'
import type { Database } from 'types/supabase'
import type { ApplicationContext } from './types'

dayjs.extend(utcPlugin)

export type ConvertedEvent = Database['public']['Tables']['events']['Row']

/** One occurrence an application is asking to be created. */
export type Occurrence = {
  start_time: Date
  finish_time: Date
}

/**
 * The event payload every occurrence shares — everything except the times and
 * the four provenance columns, which `convertApplication` owns.
 */
export type ConversionBaseEvent = Omit<
  createEventParams,
  | 'start_time'
  | 'finish_time'
  | 'source'
  | 'source_url'
  | 'source_uid'
  | 'source_application_id'
>

export type ConvertApplicationParams = {
  occurrences: Occurrence[]
  baseEvent: ConversionBaseEvent
  applicationContext: ApplicationContext
  createEvent: (params: createEventParams) => Promise<ConvertedEvent>
  findExistingOccurrence: (occurrence: Occurrence) => Promise<ConvertedEvent | null>
  markProcessed: (applicationId: number) => Promise<unknown>
  navigate: () => void
}

/** Postgres `unique_violation`. */
export const UNIQUE_VIOLATION = '23505'

const readErrorCode = (error: unknown): string | null => {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code
    return typeof code === 'string' ? code : null
  }
  return null
}

/**
 * The weekly expansion, lifted verbatim out of the form.
 *
 * Semantics are today's, deliberately unchanged: start at `start`/`finish`,
 * emit one entry, step both forward seven days, and continue while the start
 * still falls in the calendar month the first one did. Bounded by that month,
 * so at most five occurrences.
 *
 * Extracting it is what lets the expansion be asserted without rendering a
 * form, and it gives the manual-create path and the conversion path ONE
 * definition of "weekly" instead of two that can drift.
 *
 * Stepping and the month bound run in UTC, never the browser's zone. Stored
 * times are Lisbon wall-clock digits labelled +00:00, so the UTC reading IS
 * the event's local time. Adding days in Europe/Lisbon instead holds the
 * browser's wall clock across a DST change and shifts every later occurrence
 * by an hour (late after the October change, early after the March one).
 */
export const weeklyOccurrences = (start: Date, finish: Date): Occurrence[] => {
  const occurrences: Occurrence[] = []
  const firstMonth = dayjs.utc(start).month()
  let startTime = dayjs.utc(start)
  let finishTime = dayjs.utc(finish)

  // An invalid date yields NaN here, and NaN never equals itself, so a broken
  // input produces an empty list rather than an unbounded loop.
  while (startTime.month() === firstMonth) {
    occurrences.push({
      start_time: startTime.toDate(),
      finish_time: finishTime.toDate()
    })
    startTime = startTime.add(7, 'day')
    finishTime = finishTime.add(7, 'day')
  }

  return occurrences
}

/**
 * Turn an application into its events, in an order that can be relied on.
 *
 * Every side effect is injected. That is the point of the function: FBIMP-13
 * is entirely a set of claims about ORDER, and order is only provable if the
 * things being ordered are observable. Nothing here reaches for a client, a
 * translation or a toast — the caller owns the user-facing surface.
 *
 * The order, and no other:
 *
 *   1. create each occurrence, sequentially, carrying all four provenance
 *      columns;
 *   2. only then mark the application processed;
 *   3. only then navigate, exactly once.
 *
 * Sequential, not concurrent: firing every insert at once makes the weekly
 * occurrences race each other into the very unique violation this function
 * then has to disambiguate, multiplies a partial image failure, and makes the
 * ordering unobservable.
 *
 * Any failure propagates with the application still unprocessed, which is what
 * makes a conversion retryable.
 *
 * Note what this function is NOT given: a way to update an event. A
 * `(source_application_id, start_time)` conflict returns the existing row
 * untouched, and overwriting a row a curator may have since edited is not
 * expressible in this body rather than merely discouraged.
 */
export const convertApplication = async ({
  occurrences,
  baseEvent,
  applicationContext,
  createEvent,
  findExistingOccurrence,
  markProcessed,
  navigate
}: ConvertApplicationParams): Promise<ConvertedEvent[]> => {
  const events: ConvertedEvent[] = []

  for (const occurrence of occurrences) {
    let created: ConvertedEvent

    try {
      created = await createEvent({
        ...baseEvent,
        start_time: occurrence.start_time.toISOString(),
        finish_time: occurrence.finish_time.toISOString(),
        source: applicationContext.source,
        source_url: applicationContext.sourceUrl,
        source_uid: applicationContext.sourceUid,
        source_application_id: applicationContext.id
      })
    } catch (error) {
      if (readErrorCode(error) !== UNIQUE_VIOLATION) {
        throw error
      }

      const existing = await findExistingOccurrence(occurrence)

      // A conflict with no matching occurrence came from a DIFFERENT unique
      // index. Reporting it as a successful retry would mark an application
      // processed on a conflict that created nothing.
      if (!existing) {
        throw error
      }

      created = existing
    }

    events.push(created)
  }

  await markProcessed(applicationContext.id)
  navigate()

  return events
}
